import { 
  IsUUID, 
  IsString, 
  IsOptional, 
  IsEnum, 
  IsNumber, 
  Min, 
  IsDateString 
} from 'class-validator';
import { Brand, CategoryTypes, ProductCategory } from "../../utils/enums.utils";
import { 
  ProposalAttachmentsStatus, 
  ProposalCompanies, 
  ProposalItemStatus, 
  ProposalPaymentTerms, 
  ProposalPricingSummaryStatus, 
  ProposalStatus, 
  ProposalValidity, 
  ProposalWarranty 
} from "../../interface/proposals/proposals-response.interface";
import { 
  CreateProposalAttachmentsInterface, 
  CreateProposalItemsInterface, 
  CreateProposalPricingSummaryInterface, 
  CreateProposalsInterface, 
  UpdateProposalAttachmentsInterface, 
  UpdateProposalItemsInterface, 
  UpdateProposalPricingSummaryInterface, 
  UpdateProposalsInterface 
} from "../../interface/proposals/modify-proposals.interface";

export class CreateProposalsDto implements CreateProposalsInterface {

  @IsString()
  project_name!: string;

  @IsOptional()
  @IsString()
  project_code!: string | null;

  @IsEnum(CategoryTypes)
  category!: CategoryTypes;

  @IsEnum(ProductCategory)
  product_category!: ProductCategory;

  @IsEnum(ProposalCompanies)
  company!: ProposalCompanies;

  @IsDateString()
  selected_date!: string;

  @IsOptional()
  @IsString()
  customer_ref!: string | null;

  @IsOptional()
  @IsString()
  quotation_number!: string | null;

  @IsEnum(ProposalPaymentTerms)
  payment_terms!: ProposalPaymentTerms;

  @IsEnum(ProposalValidity)
  quote_validity!: ProposalValidity;

  @IsEnum(ProposalWarranty)
  warranty!: ProposalWarranty;

  @IsOptional()
  @IsString()
  company_remarks!: string | null;

  @IsEnum(ProposalStatus)
  status!: ProposalStatus;

  @IsUUID()
  company_id!: string;

  @IsOptional()
  @IsUUID()
  created_by_id!: string | null;

  @IsOptional()
  @IsUUID()
  updated_by_id!: string | null;
}

export class UpdateProposalsDto implements UpdateProposalsInterface {

  @IsUUID()
  id!: string;

  @IsOptional()
  @IsString()
  project_name?: string;

  @IsOptional()
  @IsString()
  project_code?: string | null;

  @IsOptional()
  @IsEnum(CategoryTypes)
  category?: CategoryTypes;

  @IsOptional()
  @IsEnum(ProductCategory)
  product_category?: ProductCategory;

  @IsOptional()
  @IsEnum(ProposalCompanies)
  company?: ProposalCompanies;

  @IsOptional()
  @IsDateString()
  selected_date?: string;

  @IsOptional()
  @IsString()
  customer_ref?: string | null;

  @IsOptional()
  @IsString()
  quotation_number?: string | null;

  @IsOptional()
  @IsEnum(ProposalPaymentTerms)
  payment_terms?: ProposalPaymentTerms;

  @IsOptional()
  @IsEnum(ProposalValidity)
  quote_validity?: ProposalValidity;

  @IsOptional()
  @IsEnum(ProposalWarranty)
  warranty?: ProposalWarranty;

  @IsOptional()
  @IsString()
  company_remarks?: string | null;

  @IsOptional()
  @IsEnum(ProposalStatus)
  status?: ProposalStatus;

  @IsOptional()
  @IsUUID()
  company_id?: string;

  @IsUUID()
  updated_by_id!: string | null;
}

// Attachments DTO
export class CreateProposalAttachmentsDto implements CreateProposalAttachmentsInterface {

  @IsUUID()
  proposal_id!: string;

  @IsUUID()
  attachment_id!: string;

  @IsEnum(ProposalAttachmentsStatus)
  status!: ProposalAttachmentsStatus;
}

export class UpdateProposalAttachmentsDto implements UpdateProposalAttachmentsInterface {

  @IsUUID()
  proposal_id!: string;

  @IsUUID()
  attachment_id!: string;

  @IsEnum(ProposalAttachmentsStatus)
  status!: ProposalAttachmentsStatus;
}

// Proposal Items DTO
export class CreateProposalItemsDto implements CreateProposalItemsInterface {

  @IsOptional()
  @IsString()
  item_details!: string | null;

  @IsOptional()
  @IsString()
  description!: string | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  quantity!: number | null;

  @IsOptional()
  @IsString()
  uom!: string | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  buying_vat_inc!: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  total_buying!: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  shipping_fee!: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  wsf!: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  markup!: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  sp!: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  sp_each!: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  misc!: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  tpc_price!: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  fsp!: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  fsp_each!: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  profit_each!: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  total_profit!: number | null;

  @IsOptional()
  @IsString()
  delivery!: string | null;

  @IsOptional()
  @IsString()
  project!: string | null;

  @IsOptional()
  @IsString()
  lof!: string | null;

  @IsOptional()
  @IsString()
  aof!: string | null;

  @IsOptional()
  @IsString()
  srf!: string | null;

  @IsOptional()
  @IsString()
  lsf!: string | null;

  @IsEnum(Brand)
  brand!: Brand;

  @IsUUID()
  proposal_id!: string;

  @IsOptional()
  @IsUUID()
  attachment_item_img!: string | null;

  @IsEnum(ProposalItemStatus)
  status!: ProposalItemStatus;
}

export class UpdateProposalItemsDto implements UpdateProposalItemsInterface {

  @IsUUID()
  id!: string;

  @IsOptional()
  @IsString()
  item_details?: string | null;

  @IsOptional()
  @IsString()
  description?: string | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  quantity?: number | null;

  @IsOptional()
  @IsString()
  uom?: string | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  buying_vat_inc?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  total_buying?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  shipping_fee?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  wsf?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  markup?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  sp?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  sp_each?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  misc?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  tpc_price?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  fsp?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  fsp_each?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  profit_each?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  total_profit?: number | null;

  @IsOptional()
  @IsString()
  delivery?: string | null;

  @IsOptional()
  @IsString()
  project?: string | null;

  @IsOptional()
  @IsString()
  lof?: string | null;

  @IsOptional()
  @IsString()
  aof?: string | null;

  @IsOptional()
  @IsString()
  srf?: string | null;

  @IsOptional()
  @IsString()
  lsf?: string | null;

  @IsOptional()
  @IsEnum(Brand)
  brand?: Brand;

  @IsOptional()
  @IsUUID()
  attachment_item_img?: string | null;

  @IsOptional()
  @IsEnum(ProposalItemStatus)
  status?: ProposalItemStatus;
}

// Proposal Pricing Summary DTO
export class CreateProposalPricingSummaryDto implements CreateProposalPricingSummaryInterface {

  @IsNumber()
  @Min(0)
  subtotal!: number;

  @IsNumber()
  @Min(0)
  shipping_fee!: number;

  @IsNumber()
  @Min(0)
  markup!: number;

  @IsNumber()
  @Min(0)
  misc!: number;

  @IsNumber()
  @Min(0)
  approval_discount!: number;

  @IsNumber()
  @Min(0)
  discount_percent!: number;

  @IsNumber()
  @Min(0)
  discount_amount!: number;

  @IsNumber()
  @Min(0)
  total_ex_vat!: number;

  @IsNumber()
  @Min(0)
  total_inc_vat!: number;

  @IsNumber()
  @Min(0)
  total_profit!: number;

  @IsNumber()
  @Min(0)
  grand_total!: number;

  @IsOptional()
  @IsString()
  lead_time!: string | null;

  @IsUUID()
  proposal_id!: string;

  @IsEnum(ProposalPricingSummaryStatus)
  status!: ProposalPricingSummaryStatus;
}

export class UpdateProposalPricingSummaryDto implements UpdateProposalPricingSummaryInterface {

  @IsUUID()
  id!: string;

  @IsOptional()
  @IsNumber()
  @Min(0)
  subtotal?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  shipping_fee?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  markup?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  misc?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  approval_discount?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  discount_percent?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  discount_amount?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  total_ex_vat?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  total_inc_vat?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  total_profit?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  grand_total?: number;

  @IsOptional()
  @IsString()
  lead_time?: string | null;

  @IsOptional()
  @IsEnum(ProposalPricingSummaryStatus)
  status?: ProposalPricingSummaryStatus;
}